import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const sections = [
  {
    title: '1. Quem Somos',
    content: `A MoveMind Consulting, Lda. (doravante "MoveMind", "nós" ou "nossa empresa") é uma empresa de consultoria empresarial sediada em Luanda, Angola. Este documento descreve como recolhemos, utilizamos, armazenamos e protegemos os seus dados pessoais quando visita o nosso website ou utiliza os nossos serviços.`,
  },
  {
    title: '2. Dados que Recolhemos',
    content: `Podemos recolher os seguintes tipos de dados pessoais:\n\n• **Dados de identificação:** nome completo, cargo e empresa.\n• **Dados de contacto:** endereço de email, número de telefone e morada.\n• **Dados de navegação:** endereço IP, tipo de browser, páginas visitadas e tempo de permanência no site.\n• **Dados fornecidos voluntariamente:** mensagens enviadas através do formulário de contacto, pedidos de consulta e subscrições de newsletter.`,
  },
  {
    title: '3. Como Utilizamos os Seus Dados',
    content: `Os seus dados pessoais são utilizados para:\n\n• Responder às suas solicitações e pedidos de informação.\n• Agendar e gerir consultas e reuniões.\n• Enviar comunicações de marketing, caso tenha dado o seu consentimento.\n• Melhorar a experiência de navegação no nosso website.\n• Cumprir obrigações legais e regulatórias aplicáveis em Angola.`,
  },
  {
    title: '4. Base Legal para o Tratamento',
    content: `O tratamento dos seus dados pessoais baseia-se em:\n\n• **Consentimento:** quando nos autoriza expressamente a utilizar os seus dados.\n• **Execução de contrato:** quando o tratamento é necessário para prestar os nossos serviços.\n• **Interesse legítimo:** para melhorar os nossos serviços e comunicar com clientes existentes.\n• **Obrigação legal:** quando somos obrigados por lei a tratar determinados dados.`,
  },
  {
    title: '5. Partilha de Dados com Terceiros',
    content: `A MoveMind não vende, aluga nem partilha os seus dados pessoais com terceiros para fins comerciais. Podemos partilhar dados com:\n\n• Prestadores de serviços que nos apoiam na operação do website e dos nossos serviços (ex.: alojamento web, email marketing).\n• Autoridades competentes, quando exigido por lei ou ordem judicial.\n\nTodos os terceiros com quem partilhamos dados estão sujeitos a obrigações de confidencialidade.`,
  },
  {
    title: '6. Retenção de Dados',
    content: `Conservamos os seus dados pessoais apenas pelo tempo necessário para os fins para os quais foram recolhidos, ou conforme exigido por lei. Em regra:\n\n• Dados de contacto e consultas: até 5 anos após o último contacto.\n• Dados de newsletter: até à revogação do consentimento.\n• Dados de navegação: até 12 meses.`,
  },
  {
    title: '7. Os Seus Direitos',
    content: `Tem o direito de:\n\n• **Acesso:** solicitar uma cópia dos seus dados pessoais que tratamos.\n• **Rectificação:** corrigir dados incorrectos ou incompletos.\n• **Eliminação:** solicitar a eliminação dos seus dados ("direito ao esquecimento").\n• **Oposição:** opor-se ao tratamento dos seus dados para fins de marketing.\n• **Portabilidade:** receber os seus dados num formato estruturado e legível.\n\nPara exercer qualquer um destes direitos, contacte-nos através de geral@movemind.ao.`,
  },
  {
    title: '8. Segurança dos Dados',
    content: `Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais contra acesso não autorizado, perda, destruição ou divulgação. No entanto, nenhum sistema de transmissão de dados pela internet é 100% seguro.`,
  },
  {
    title: '9. Cookies',
    content: `O nosso website pode utilizar cookies para melhorar a experiência de navegação. Os cookies são pequenos ficheiros de texto armazenados no seu dispositivo. Pode configurar o seu browser para recusar cookies, mas isso pode afectar algumas funcionalidades do site.`,
  },
  {
    title: '10. Alterações a Esta Política',
    content: `Reservamo-nos o direito de actualizar esta Política de Privacidade a qualquer momento. Quaisquer alterações serão publicadas nesta página com a data de actualização. Recomendamos que reveja esta política periodicamente.`,
  },
  {
    title: '11. Contacto',
    content: `Para questões relacionadas com a protecção dos seus dados pessoais, contacte-nos:\n\n• **Email:** geral@movemind.ao\n• **Telefone:** +244 925 434 499\n• **Morada:** Av. Pedro de Castro Van-Dúnem, Ed. 24, 1º Andar, Apt 11, Bairro Miramar Luanda – Angola`,
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F2EE]">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#011820] pt-36 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 border border-[#026A86]/40 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#026A86] text-xs font-medium tracking-widest uppercase">
              Legal
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
            Política de <span className="text-[#026A86]">Privacidade</span>
          </h1>
          <p className="text-white/50 text-base">
            Última actualização: Janeiro de 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">

          {/* Intro card */}
          <div className="bg-white rounded-2xl p-8 mb-10 border border-[#011820]/8">
            <p className="text-[#011820]/70 text-base leading-relaxed">
              A MoveMind Consulting compromete-se a proteger a sua privacidade e a tratar os seus dados pessoais com total transparência e responsabilidade. Esta política explica como recolhemos e utilizamos as suas informações quando interage connosco.
            </p>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-6">
            {sections.map((section) => (
              <div key={section.title} className="bg-white rounded-2xl p-8 border border-[#011820]/8">
                <h2 className="text-lg font-bold text-[#011820] mb-4">{section.title}</h2>
                <div className="text-[#011820]/65 text-sm leading-relaxed whitespace-pre-line">
                  {section.content.split('\n').map((line, i) => {
                    if (line.startsWith('• **') && line.includes(':**')) {
                      const parts = line.replace('• **', '').split(':**');
                      return (
                        <p key={i} className="mb-2">
                          <span className="text-[#011820]/40 mr-1">•</span>
                          <strong className="text-[#011820]/80">{parts[0]}:</strong>
                          {parts[1]}
                        </p>
                      );
                    }
                    if (line.startsWith('• ')) {
                      return (
                        <p key={i} className="mb-2">
                          <span className="text-[#026A86] mr-2">•</span>
                          {line.replace('• ', '')}
                        </p>
                      );
                    }
                    if (line === '') return <br key={i} />;
                    return <p key={i} className="mb-2">{line}</p>;
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-12 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#026A86] text-sm font-medium hover:underline cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Voltar ao início
            </Link>
            <Link
              to="/termos-de-uso"
              className="inline-flex items-center gap-2 border border-[#026A86] text-[#026A86] text-sm font-medium px-5 py-2 rounded-full hover:bg-[#026A86] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Ver Termos de Uso
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
